import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { RoomOrnament, RoomPlacement } from '@/Data/World';
import { useOrnament, usePlacement } from '@/Recoil/World';
import { GRID_SIZE, CELL_SIZE, HORIZONTAL_PADDING, TOP_PADDING, DEFAULT_WALLPAPER_ID, gridToPixel } from './gridUtils';
import LoadingCircle from '@/Components/LoadingCircle';
import FlipIcon from '@mui/icons-material/Flip';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import './RoomEditor.scss';

interface RoomEditorProps {
    addressid: string;
    onClose?: () => void;
}

const RoomEditor: React.FC<RoomEditorProps> = ({ addressid, onClose }) => {
    const { ornaments, wallpapers, ownedIds, isLoaded: ornamentsLoaded, fetchOrnaments, fetchOwnerships } = useOrnament();
    const { placementsByUser, loading: placementsLoading, fetchPlacements, updatePlacement, deletePlacement } = usePlacement();

    const placements = useMemo(() => placementsByUser[addressid] || [], [placementsByUser, addressid]);
    const loading = !ornamentsLoaded || placementsLoading;

    const [saving, setSaving] = useState(false);
    const [selectedOrnamentId, setSelectedOrnamentId] = useState<string | null>(null);
    const [showWallpaperPicker, setShowWallpaperPicker] = useState(false);
    const [draggingOrnament, setDraggingOrnament] = useState<RoomOrnament | null>(null);

    // Fetch all data
    useEffect(() => {
        fetchOrnaments();
        fetchOwnerships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (addressid) {
            fetchPlacements(addressid);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressid]);

    // Get active wallpaper
    const activeWallpaper = useMemo(() => {
        const wallpaperPlacement = placements.find(p => p.ornamentid in wallpapers);
        if (wallpaperPlacement) { return wallpapers[wallpaperPlacement.ornamentid]; }
        return wallpapers[DEFAULT_WALLPAPER_ID];
    }, [placements, wallpapers]);

    // Filter ornament placements (exclude wallpapers)
    const ornamentPlacements = useMemo(() => {
        return placements.filter(p => p.ornamentid in ornaments);
    }, [placements, ornaments]);

    // Get selected placement
    const selectedPlacement = useMemo(() => {
        if (!selectedOrnamentId) { return null; }
        return placements.find(p => p.ornamentid === selectedOrnamentId) || null;
    }, [selectedOrnamentId, placements]);

    // Available ornaments (owned but not placed)
    const availableOrnaments = useMemo(() => {
        const placedIds = new Set(placements.map(p => p.ornamentid));
        return ownedIds
            .filter(id => !placedIds.has(id) && ornaments[id])
            .map(id => ornaments[id]);
    }, [ownedIds, placements, ornaments]);

    const getPlacementStyle = (placement: RoomPlacement): React.CSSProperties => {
        const ornament = ornaments[placement.ornamentid];
        if (!ornament) { return {}; }

        const { left, top } = gridToPixel(placement.x, placement.y);

        return {
            position: 'absolute',
            left,
            top,
            width: `${ornament.width * CELL_SIZE}px`,
            height: `${ornament.height * CELL_SIZE}px`,
            transform: placement.direction === 'left' ? 'scaleX(-1)' : 'none',
            zIndex: 10
        };
    };

    // Handle flip
    const handleFlip = useCallback(async () => {
        if (!selectedPlacement || saving) { return; }
        setSaving(true);

        const newDirection = selectedPlacement.direction === 'left' ? 'right' : 'left';
        await updatePlacement({
            ornamentid: selectedPlacement.ornamentid,
            x: selectedPlacement.x,
            y: selectedPlacement.y,
            direction: newDirection
        });

        setSaving(false);
    }, [selectedPlacement, saving, updatePlacement]);

    // Handle delete
    const handleDelete = useCallback(async () => {
        if (!selectedPlacement || saving) { return; }
        setSaving(true);

        const success = await deletePlacement(selectedPlacement.ornamentid);
        if (success) { setSelectedOrnamentId(null); }

        setSaving(false);
    }, [selectedPlacement, saving, deletePlacement]);

    // Handle wallpaper change
    const handleWallpaperChange = useCallback(async (wallpaperId: string) => {
        if (saving) { return; }
        setSaving(true);

        // Remove old wallpaper placement
        const oldWallpaperPlacement = placements.find(p => p.ornamentid in wallpapers);
        if (oldWallpaperPlacement) {
            await deletePlacement(oldWallpaperPlacement.ornamentid);
        }

        // Add new wallpaper
        await updatePlacement({
            ornamentid: wallpaperId,
            x: 0,
            y: 0,
            direction: 'right'
        });

        setShowWallpaperPicker(false);
        setSaving(false);
    }, [placements, wallpapers, saving, deletePlacement, updatePlacement]);

    // Handle drag start from palette
    const handleDragStart = (ornament: RoomOrnament) => (e: React.DragEvent) => {
        setDraggingOrnament(ornament);
        e.dataTransfer.effectAllowed = 'move';
    };

    // Handle drop on grid
    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        if (!draggingOrnament || saving) { return; }

        const rect = e.currentTarget.getBoundingClientRect();
        const dropX = e.clientX - rect.left;
        const dropY = e.clientY - rect.top;

        // Convert to grid coordinates
        const gridX = Math.round(dropX / CELL_SIZE) - 6;
        const gridY = 6 - Math.round(dropY / CELL_SIZE);

        // Clamp to grid bounds
        const clampedX = Math.max(-6, Math.min(6, gridX));
        const clampedY = Math.max(-6, Math.min(6, gridY));

        setSaving(true);
        await updatePlacement({
            ornamentid: draggingOrnament.ornamentid,
            x: clampedX,
            y: clampedY,
            direction: 'right'
        });

        setDraggingOrnament(null);
        setSaving(false);
    }, [draggingOrnament, saving, updatePlacement]);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const roomWidth = GRID_SIZE * CELL_SIZE + HORIZONTAL_PADDING * 2;

    if (loading) {
        return (
            <div className="RoomEditor Loading">
                <LoadingCircle show />
            </div>
        );
    }

    return (
        <div className="RoomEditor">
            {/* Header */}
            <div className="EditorHeader">
                <h3>Edit Room</h3>
                <button className="WallpaperButton" onClick={() => setShowWallpaperPicker(true)}>
                    <WallpaperIcon /> Wallpaper
                </button>
                {onClose && (
                    <button className="CloseButton" onClick={onClose}>
                        <CloseIcon />
                    </button>
                )}
            </div>

            {/* Room Preview */}
            <div className="RoomPreview" style={{ width: roomWidth }}>
                {activeWallpaper && (
                    <img src={activeWallpaper.image} alt="Wallpaper" className="Wallpaper" />
                )}

                <div
                    className="OrnamentLayer"
                    style={{
                        left: HORIZONTAL_PADDING,
                        top: TOP_PADDING,
                        width: GRID_SIZE * CELL_SIZE,
                        height: GRID_SIZE * CELL_SIZE
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                >
                    {ornamentPlacements.map(placement => {
                        const ornament = ornaments[placement.ornamentid];
                        if (!ornament) { return null; }
                        const isSelected = selectedOrnamentId === placement.ornamentid;
                        return (
                            <div
                                key={placement.ornamentid}
                                className={`PlacedOrnament ${isSelected ? 'Selected' : ''}`}
                                style={getPlacementStyle(placement)}
                                onClick={() => setSelectedOrnamentId(isSelected ? null : placement.ornamentid)}
                            >
                                <img src={ornament.image} alt={ornament.name} className="OrnamentImage" />
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Action Bar for selected ornament */}
            {selectedPlacement && (
                <div className="ActionBar">
                    <button className="ActionButton" onClick={handleFlip} disabled={saving}>
                        <FlipIcon /> Flip
                    </button>
                    <button className="ActionButton Delete" onClick={handleDelete} disabled={saving}>
                        <DeleteIcon /> Remove
                    </button>
                </div>
            )}

            {/* Ornament Palette */}
            <div className="OrnamentPalette">
                <h4>Your Ornaments</h4>
                <div className="PaletteGrid">
                    {availableOrnaments.length === 0 ? (
                        <p className="EmptyText">No available ornaments</p>
                    ) : (
                        availableOrnaments.map(ornament => (
                            <div
                                key={ornament.ornamentid}
                                className="PaletteItem"
                                draggable
                                onDragStart={handleDragStart(ornament)}
                            >
                                <img src={ornament.image} alt={ornament.name} />
                                <span>{ornament.name}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Wallpaper Picker Modal */}
            {showWallpaperPicker && (
                <div className="ModalOverlay" onClick={() => setShowWallpaperPicker(false)}>
                    <div className="WallpaperPicker" onClick={e => e.stopPropagation()}>
                        <h4>Choose Wallpaper</h4>
                        <div className="WallpaperGrid">
                            {Object.values(wallpapers).map(wp => (
                                <div
                                    key={wp.ornamentid}
                                    className={`WallpaperItem ${activeWallpaper?.ornamentid === wp.ornamentid ? 'Active' : ''}`}
                                    onClick={() => handleWallpaperChange(wp.ornamentid)}
                                >
                                    <img src={wp.image} alt={wp.name} />
                                </div>
                            ))}
                        </div>
                        <button className="ClosePickerButton" onClick={() => setShowWallpaperPicker(false)}>
                            Close
                        </button>
                    </div>
                </div>
            )}

            {saving && <div className="SavingOverlay"><LoadingCircle show /></div>}
        </div>
    );
};

export default RoomEditor;
